import { useCallback, useMemo, useState } from 'react';

import {
  CampaignStoreItem,
  EventDate,
  EventDatesResponse,
  EventStoreItem,
  useListEventDates,
  useListTickets,
  useShowConsumerCampaignById,
  useShowConsumerCampaignSettingsById,
} from '@givebacks/api/store';
import {
  CalendarSection,
  EventDateTimeCard,
  EventLocation,
  GetTicketsCard,
  GetTicketsModal,
  SeasonTicketsSection,
  TicketsSection,
} from '@givebacks/shop';
import { Constants, useCauseParam, usePlatformShare } from 'core';
import { EndedCampaignAlert, ShareModal } from 'core/Components';
import { BASE_URL } from 'core/Utils';
import { DateTime } from 'luxon';
import { useNavigate } from 'react-router-dom';
import { RichTextDisplay } from 'rich-text';
import {
  Alert,
  Box,
  ButtonMantine,
  Container,
  MobileBottomButton,
  Skeleton,
  Stack,
  Text,
  Title,
} from 'ui';
import { IconShare, IconTicket } from 'ui/Icons';

import { useCrowdfundingProvider } from '../../../Context';
import { HeaderImage } from '../../components';
import classes from '../../components/CausePageLayout.module.css';
import { Benefits } from '../Campaigns/CampaignPage/Benefits';

const EventShareButton = ({ event, causeId }: { event?: CampaignStoreItem; causeId?: string }) => {
  const { data: cause } = useCauseParam();
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const { platformShare } = usePlatformShare();
  if (!event || !causeId) return null;

  const eventPageLink = `${BASE_URL}${Constants.appRoutes.event(
    cause?.shortener ?? causeId,
    event?.shortener ?? event?.uuid
  )}`;

  return (
    <>
      <ButtonMantine
        size="md"
        variant="white"
        color="gray"
        leftSection={<IconShare />}
        onClick={() =>
          void platformShare(
            {
              share_options: { url: eventPageLink },
            },
            () => setIsShareModalOpen(true)
          )
        }
      >
        Share
      </ButtonMantine>
      <ShareModal
        opened={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        causeName={cause?.name}
        link={eventPageLink}
        type="event"
        imgSrc={cause?.logo?.url}
      />
    </>
  );
};

const EventPage = ({
  EventMap,
}: {
  EventMap?: React.JSXElementConstructor<Record<string, unknown>>;
}) => {
  const navigate = useNavigate();
  const { campaignId, causeId } = useCrowdfundingProvider();
  const [isGetTicketsModalOpen, setIsGetTicketsModalOpen] = useState(false);
  const { data: settings } = useShowConsumerCampaignSettingsById({ causeId, campaignId });

  // get event data, including inventory on `event_date` for single date events
  const { data, isLoading: isEventLoading } = useShowConsumerCampaignById({
    causeId,
    campaignId,
    queryOptions: {
      options: {
        include: 'inventory',
      },
    },
  });

  const event = data as EventStoreItem;

  const eventDescription = useMemo(() => {
    return (
      !!event?.description && (
        <Skeleton visible={isEventLoading}>
          <Stack>
            <Title order={2}>Description</Title>
            <Text c="gray.8" type="body" style={{ whiteSpace: 'pre-wrap' }}>
              <RichTextDisplay html={event.description} />
            </Text>
          </Stack>
        </Skeleton>
      )
    );
  }, [event?.description, isEventLoading]);

  const { data: seasonTicketsData } = useListTickets({
    eventId: campaignId,
    causeId,
    ticketsType: 'season_passes',
  });

  const hasSeasonTickets = useMemo(() => {
    return seasonTicketsData?.store_items && seasonTicketsData.store_items.length > 0;
  }, [seasonTicketsData?.store_items]);

  const { data: eventDates } = useListEventDates({
    eventId: campaignId,
    causeId,
    include: 'event_date_tickets,inventory',
    select: useCallback((data: EventDatesResponse<EventDate[]>) => data.event_dates, []),
  });

  // show tickets section if there is a single event date and the campaign is not read only
  const showTicketsSection = event?.event_date && !settings?.isCampaignReadOnly;
  // show calendar section if multi date and the campaign is not read only
  const showCalendarSection = !showTicketsSection && !settings?.isCampaignReadOnly;

  const allTicketsExpired = useMemo(() => {
    // All tickets are expired if every ticket type for every event date has expired
    const allEventTicketsExpired = eventDates?.every(
      (eventDate) =>
        eventDate?.event_date_tickets?.every((ticket) => {
          if (!ticket.store_item.expires_at) return true;

          const now = DateTime.now();
          const expiresAt = DateTime.fromISO(ticket.store_item.expires_at);

          return now >= expiresAt;
        })
    );

    const allSeasonTicketsExpired = seasonTicketsData?.store_items?.every((ticket) => {
      if (!ticket.expires_at) return true;
      const now = DateTime.now();
      const expiresAt = DateTime.fromISO(ticket.expires_at);
      return now >= expiresAt;
    });

    return allEventTicketsExpired && allSeasonTicketsExpired;
  }, [eventDates, seasonTicketsData?.store_items]);

  const customSections = useMemo(() => {
    return event?.sections?.map((section, index) => (
      <Stack key={index}>
        <Title order={2}>{section.title}</Title>
        <Text c="gray.8" type="body" style={{ whiteSpace: 'pre-wrap' }}>
          <RichTextDisplay html={section?.description} />
        </Text>
      </Stack>
    ));
  }, [event?.sections]);

  const singleEventDateCapacity = useMemo(() => {
    if (!event?.single_date || !event?.event_date) return null;
    return event?.event_date?.inventory?.remaining_capacity;
  }, [event]);

  const showLimitedCapacityAlert = useMemo(() => {
    return (
      // We should show the limited capacity alert if:
      // If all tickets are NOT expired
      !allTicketsExpired &&
      // and single event date capacity is not null or undefined and is a number
      typeof singleEventDateCapacity === 'number' &&
      // and single event date capacity is greater than 0
      singleEventDateCapacity > 0 &&
      // and single event date capacity is less than or equal to 20
      singleEventDateCapacity <= 20
    );
  }, [allTicketsExpired, singleEventDateCapacity]);

  return (
    <Container
      fluid
      className={classes.container}
      style={{
        // Set Mobile Button Bottom Height via local container style
        '--mobile-bottom-button-height': '80px',
      }}
    >
      <Stack className={classes.leftContent}>
        <HeaderImage
          isLoading={isEventLoading}
          src={
            event?.images?.[0]?.url ||
            'https://s3.amazonaws.com/com.givebacks.public/images/crowdfunding/campaign-default.png'
          }
          headerButtons={<EventShareButton event={event} causeId={causeId} />}
          backTo={'../..'}
        />
        <Skeleton visible={isEventLoading}>
          <Title order={1} c="gray.8">
            {event?.name}
          </Title>
        </Skeleton>

        <EventDateTimeCard
          startingAt={event?.earliest_starting_at}
          endingAt={event?.latest_ending_at}
        />

        <Alert.Collapse
          in={showLimitedCapacityAlert}
          type="warning"
          icon={<IconTicket />}
          title={`Only ${singleEventDateCapacity} spots left!`}
        >
          This event is almost at capacity--don&apos;t miss your chance!
        </Alert.Collapse>

        {eventDescription}

        {settings?.isCampaignReadOnly && <EndedCampaignAlert type="event" hiddenFrom="md" />}

        {!!event?.location && <EventLocation eventLocation={event.location} EventMap={EventMap} />}

        {customSections}

        {event && event.benefit_options?.enabled && (
          <Benefits
            campaignId={event.uuid}
            causeId={event.cause_id}
            benefitsTitle={event.benefit_options?.title}
          />
        )}

        {showTicketsSection && (
          <TicketsSection
            eventDateId={event?.event_date?.uuid}
            causeId={causeId}
            eventId={campaignId}
            title={<Title order={2}>Tickets</Title>}
          />
        )}

        {hasSeasonTickets && <SeasonTicketsSection eventId={campaignId} causeId={causeId} />}

        {showCalendarSection && <CalendarSection causeId={causeId} eventId={campaignId} />}
      </Stack>

      <Box className={classes.rightContent}>
        <Stack className={classes.rightContentChild}>
          <GetTicketsCard
            causeId={causeId}
            eventId={event?.uuid}
            setIsGetTicketsModalOpen={
              showTicketsSection
                ? () => navigate(`event-date/${event?.event_date?.uuid}`)
                : setIsGetTicketsModalOpen
            }
            isAllTicketsExpired={allTicketsExpired}
          />
        </Stack>
      </Box>

      <GetTicketsModal
        opened={isGetTicketsModalOpen}
        onClose={() => setIsGetTicketsModalOpen(false)}
        eventId={campaignId}
        causeId={causeId}
      />

      {!settings?.isCampaignReadOnly && (
        <MobileBottomButton>
          <ButtonMantine
            onClick={() => setIsGetTicketsModalOpen(true)}
            disabled={allTicketsExpired}
          >
            Get Tickets
          </ButtonMantine>
        </MobileBottomButton>
      )}
    </Container>
  );
};

export { EventPage };
